import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolSpectrumData} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "../../../../common/types";
import {Box, Loader, Text, Select} from "@mantine/core";

interface SpectrumChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const SpectrumChart: React.FC<SpectrumChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [spectrumData, setSpectrumData] = useState<PlotParams>()
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedTime, setSelectedTime] = useState<string | null>(null)
    const [selectedChannel, setSelectedChannel] = useState<string>("all")
    const [timeOptions, setTimeOptions] = useState<{ value: string; label: string }[]>([])
    const [channelOptions, setChannelOptions] = useState<{ value: string; label: string }[]>([])
    const [rawData, setRawData] = useState<[string, string, number[], number[]][]>([])
    const [figureLayout, setFigureLayout] = useState<any>(null)
    const [traceTemplate, setTraceTemplate] = useState<any>(null)


    // Load data once when protocol changes
    useEffect(() => {
        if (selectedProtocol) {
            setLoading(true)
            loadProtocolSpectrumData(activeCompany.id, selectedProtocol).then((data: any) => {
                // data.data is expected to be an array of [time, channel, frequencies, power_db]
                // Store the raw data for filtering later
                setRawData(data.data);

                // Store the figure layout from server
                if (data.figure && data.figure.layout) {
                    setFigureLayout(data.figure.layout);
                }

                // Store the trace template from server
                if (data.figure && data.figure.data && data.figure.data[0]) {
                    setTraceTemplate(data.figure.data[0]);
                }

                // Extract unique times and channels for the dropdowns
                const times = new Set<string>();
                const channels = new Set<string>();

                data.data.forEach(([time, channel, _frequencies, _power]: [string, string, number[], number[]]) => {
                    times.add(time);
                    channels.add(channel);
                });

                const timeOpts = Array.from(times).map((time) => ({
                    value: time,
                    label: new Date(time).toLocaleString()
                }));

                const channelOpts = [
                    { value: "all", label: "All Channels" },
                    ...Array.from(channels).sort().map((channel) => ({
                        value: channel,
                        label: channel
                    }))
                ];

                setTimeOptions(timeOpts);
                setChannelOptions(channelOpts);

                // Set default time to first available
                if (timeOpts.length > 0) {
                    setSelectedTime(timeOpts[0].value);
                }
            }).catch(() => {
                // eat the error since notification would already be called
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [selectedProtocol, refreshTrigger])

    // Update chart when selections change
    useEffect(() => {
        if (rawData.length > 0 && selectedTime) {
            // Filter data for the selected time and channel(s)
            const filteredData = rawData.filter(([time, channel, _frequencies, _power]: [string, string, number[], number[]]) => {
                const timeMatch = time === selectedTime;
                const channelMatch = selectedChannel === "all" || channel === selectedChannel;
                return timeMatch && channelMatch;
            });

            // Create a trace for each channel
            filteredData.sort(([, a], [, b]) => a.localeCompare(b));

            const traces = filteredData.map(([_time, channel, frequencies, power]: [string, string, number[], number[]]) => ({
                ...(traceTemplate || {}),
                name: channel,
                x: frequencies,
                y: power,
            }));

            setSpectrumData({
                layout: {
                    ...figureLayout,
                },
                config: { responsive: true },
                data: traces
            })
        }
    }, [rawData, selectedTime, selectedChannel])

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Time</Text>
                    <Select
                        data={timeOptions}
                        value={selectedTime}
                        onChange={(v) => setSelectedTime(v)}
                        style={{width: 220}}
                        disabled={timeOptions.length === 0}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Channel</Text>
                    <Select
                        data={channelOptions}
                        value={selectedChannel}
                        onChange={(v) => setSelectedChannel(v || "all")}
                        style={{width: 220}}
                        disabled={channelOptions.length === 0}
                    />
                </Box>
            </Box>

            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {spectrumData ? (
                    <Box style={{height: '100%', width: '100%'}}>
                        <PlotlyPlot {...spectrumData}/>
                        {loading ? renderLoading() : null}
                    </Box>
                ) : (
                    <Box style={{height: '100%', position: 'relative'}}>
                        {renderLoading()}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

